import { useState } from 'react';
import {
  COMMITTEE_ORGANIZATION_TYPE_LABELS,
  FILING_JURISDICTION_LABELS,
  SIGNER_ROLE_LABELS,
  type CommitteeFactsDto,
} from '@signalvote/shared';
import { useCommitteeFacts } from '@/hooks/queries';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface FactRow {
  label: string;
  value: string | null | undefined;
}

/** In the operator's own zone, named, since the queue spans campaigns in every zone. */
function formatAttestedAt(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(iso));
}

function buildRows({ facts, attestation, attestationStatement }: CommitteeFactsDto) {
  const organizationType = facts.organizationType
    ? facts.organizationType === 'Other' && facts.organizationTypeOther
      ? `Other: ${facts.organizationTypeOther}`
      : COMMITTEE_ORGANIZATION_TYPE_LABELS[facts.organizationType]
    : null;
  const cityLine = [facts.city, [facts.state, facts.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const address = [facts.street, cityLine].filter(Boolean).join(', ');
  const signerRole = attestation?.signerRole
    ? attestation.signerRole === 'Other' && attestation.signerRoleOther
      ? `Other: ${attestation.signerRoleOther}`
      : SIGNER_ROLE_LABELS[attestation.signerRole]
    : null;

  const required: FactRow[] = [
    { label: 'Committee name as filed', value: facts.legalName },
    { label: 'EIN', value: facts.ein },
    { label: 'Filing phone', value: facts.filingPhone },
    { label: 'Filing email', value: facts.filingEmail },
  ];
  const optional: FactRow[] = [
    { label: 'Organization type', value: organizationType },
    { label: 'Files with', value: facts.filingJurisdiction ? FILING_JURISDICTION_LABELS[facts.filingJurisdiction] : null },
    { label: 'Committee ID', value: facts.committeeFilerId },
    { label: 'Office or race', value: facts.officeSought },
    { label: 'Verification code goes to', value: facts.verificationContactName },
    { label: 'Treasurer', value: facts.treasurerName },
    { label: 'Address', value: address },
    { label: 'Website', value: facts.website },
    { label: 'Opt-in description', value: facts.optInDescription },
    { label: 'Sample message 1', value: facts.sampleMessage },
    { label: 'Sample message 2', value: facts.sampleMessage2 },
  ];
  const signer: FactRow[] = [
    { label: 'Signer', value: attestation?.signerName },
    { label: 'Signer role', value: signerRole },
    { label: 'Signer email', value: attestation?.signerEmail },
    { label: 'Attested at', value: attestation?.attestedAt && formatAttestedAt(attestation.attestedAt) },
    { label: 'Attestation statement', value: attestationStatement },
  ];
  return { required, optional, signer };
}

function CopyButton({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Button variant="ghost" size="sm" aria-label={`Copy ${label}`} onClick={copy}>
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

function FactList({ title, rows }: { title: string; rows: FactRow[] }) {
  return (
    <div className="space-y-1">
      <h3 className="text-sm font-medium">{title}</h3>
      <dl className="divide-y divide-border rounded-md border border-border text-sm">
        {rows.map(({ label, value }) => (
          <div key={label} className="flex items-start gap-3 p-2">
            <dt className="w-44 shrink-0 text-muted-foreground">{label}</dt>
            <dd className="min-w-0 flex-1 whitespace-pre-wrap break-words">
              {value ? value : <span className="text-muted-foreground">Not given</span>}
            </dd>
            {value ? <CopyButton label={label} value={value} /> : null}
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Every fact the campaign entered, laid out one value per row so the operator can copy
 * each into Campaign Verify. Read-only: the campaign edits its own facts, and an operator
 * edit would put words in the signer's mouth under their attestation.
 */
export function CommitteeFactsCard() {
  const { data, isLoading } = useCommitteeFacts();

  return (
    <section aria-label="Committee facts">
      <Card>
        <CardHeader>
          <CardTitle>Committee facts</CardTitle>
          <CardDescription>
            What the campaign entered, read-only. Copy each value into Campaign Verify.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading || !data ? (
            <p className="text-sm text-muted-foreground">Loading committee facts…</p>
          ) : (
            (() => {
              const { required, optional, signer } = buildRows(data);
              return (
                <>
                  <FactList title="Required" rows={required} />
                  <FactList title="Optional" rows={optional} />
                  <FactList title="Attestation" rows={signer} />
                </>
              );
            })()
          )}
        </CardContent>
      </Card>
    </section>
  );
}
